<template>
  <div>
    <div class="ma-2">
      <v-btn
        color="primary"
        class="mb-2"
        @click="openDialog()"
        id="users__open-dialog"
        :disabled="!isPermitted(PermissionEnum.USERS.ADD)"
        >{{ $t("modules.users.add") }}</v-btn
      >
    </div>
    <v-dialog
      v-model="dialog"
      max-width="800px"
      persistent
      id="users__dialog"
      v-if="
        isPermitted(PermissionEnum.USERS.ADD) ||
          isPermitted(PermissionEnum.USERS.UPDATE)
      "
    >
      <v-card>
        <v-form ref="userForm" @submit.prevent="save()">
          <v-card-title>
            <span class="headline">{{ formTitle }}</span>
          </v-card-title>

          <v-card-text>
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs12 sm12 md6>
                  <v-text-field
                    v-model="username"
                    :label="$t('modules.users.username')"
                    id="users__form-username"
                    :rules="[
                      v =>
                        !!v ||
                        $t('validation.required', [
                          $t('modules.users.username')
                        ])
                    ]"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm12 md6>
                  <v-text-field
                    :type="isShowPassword ? 'text' : 'password'"
                    v-model="password"
                    :placeholder="editedIndex > -1 ? '******' : ''"
                    :label="$t('modules.users.password')"
                    id="users__form-password"
                    :rules="[
                      v =>
                        !!v ||
                        editedIndex !== -1 ||
                        $t('validation.required', [
                          $t('modules.users.password')
                        ]),
                      ...passwordRegexRules
                    ]"
                    :append-icon="isShowPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="isShowPassword = !isShowPassword"
                  >
                    <template v-slot:message="{ message, key }">
                      <div
                        v-html="message"
                        :key="key"
                        class="custom_error_message"
                      ></div>
                    </template>
                  </v-text-field>
                </v-flex>
                <v-flex xs12 sm12 md6>
                  <v-text-field
                    type="email"
                    v-model="email"
                    :label="$t('modules.users.email')"
                    id="users__form-email"
                    :rules="[
                      v =>
                        !!v ||
                        $t('validation.required', [$t('modules.users.email')]),
                      ...emailRules
                    ]"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm12 md12>
                  <v-textarea
                    type="text"
                    v-model="description"
                    :label="$t('modules.users.description')"
                    rows="3"
                    id="users__form-description"
                  ></v-textarea>
                </v-flex>
                <v-flex xs12 sm12 md12 pa-0 v-if="environment_manager_edited">
                  <v-alert
                    :value="true"
                    color="warning"
                    icon="mdi-alert"
                    outlined
                    >{{ $t("error.notAllowed") }}</v-alert
                  >
                </v-flex>
                <permission-section v-else></permission-section>
              </v-layout>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="secondary"
              text
              elevation="1"
              @click="close"
              id="users__cancel"
              >{{ $t("actions.cancel") }}</v-btn
            >
            <v-btn
              color="secondary"
              text
              elevation="1"
              type="submit"
              :loading="is_loading"
              :disabled="is_loading_global"
              id="users__save"
            >
              {{ editedIndex === -1 ? $t("actions.add") : $t("actions.save") }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PermissionSection from "./permission-section";
import { handleError } from "@/utils/error";
import { userMixins } from "../mixins";
import { permissionMixin } from "@/mixins/permissions";

export default {
  name: "UsersAction",
  components: { PermissionSection },
  mixins: [userMixins, permissionMixin],
  data() {
    return {
      isShowPassword: false
    };
  },
  computed: {
    users() {
      return this.$store.state.users.users;
    },
    dialog: {
      get() {
        return this.$store.state.users.dialog;
      },
      set(val) {
        this.$store.dispatch("users/toggleDialog", val);
      }
    },
    formTitle() {
      return this.$store.state.users.formTitle;
    },
    editedItem() {
      return this.$store.state.users.editedItem;
    },
    editedIndex() {
      return this.$store.state.users.editedIndex;
    },
    username: {
      get() {
        return this.$store.state.users.editedItem.username;
      },
      set(val) {
        this.$store.commit("users/updateUsername", val);
      }
    },
    password: {
      get() {
        return this.$store.state.users.editedItem.password;
      },
      set(val) {
        this.$store.commit("users/updatePassword", val);
      }
    },
    email: {
      get() {
        return this.$store.state.users.editedItem.email;
      },
      set(val) {
        this.$store.commit("users/updateEmail", val);
      }
    },
    description: {
      get() {
        return this.$store.state.users.editedItem.description;
      },
      set(val) {
        this.$store.commit("users/updateDescription", val);
      }
    },
    permissions: {
      get() {
        return this.$store.state.users.editedItem.permissions;
      },
      set(val) {
        this.$store.commit("users/updatePermissions", val);
      }
    },
    severity: {
      get() {
        return this.$store.state.users.editedItem.severity;
      },
      set(val) {
        this.$store.commit("users/updateSeverity", val);
      }
    },
    is_disabled: {
      get() {
        return this.$store.state.users.editedItem.is_disabled;
      },
      set(val) {
        this.$store.commit("users/updateIsDisabled", val);
      }
    },
    search: {
      get() {
        return this.$store.state.users.search;
      },
      set(val) {
        this.$store.commit("users/updateSearch", val);
      }
    },
    is_loading() {
      return this.$store.state.loader.is_loading;
    },
    is_loading_global() {
      return this.$store.state.loader.is_loading_global;
    },
    environment_manager_edited() {
      return this.$store.state.users.editedItem.environment_manager;
    }
  },

  methods: {
    openDialog() {
      this.$store.commit("users/updateIndex", -1);
      this.$store.commit("users/updateFormTitle", 1);
      this.$store.dispatch("users/toggleDialog", true).then(() => {
        this.$refs.userForm.resetValidation();
      });
    },

    save() {
      if (this.$refs.userForm.validate()) {
        this.$store.dispatch("loader/startLoading");
        if (this.editedIndex > -1) {
          let data = {
            id: this.users[this.editedIndex].id,
            username: this.username,
            password: this.password,
            email: this.email,
            description: this.description,
            permissions: this.permissions,
            severity: this.severity,
            is_disabled: this.is_disabled
          };
          this.$store.dispatch("users/update", data).catch(error => {
            handleError(error);
          });
        } else {
          let data = {
            username: this.username,
            password: this.password,
            email: this.email,
            description: this.description,
            permissions: this.permissions,
            severity: this.severity,
            is_disabled: this.is_disabled
          };
          this.$store.dispatch("users/add", data).catch(error => {
            handleError(error);
          });
        }
      }
    },

    close() {
      this.$store.dispatch("users/toggleDialog", false).then(response => {
        this.$refs.userForm.resetValidation();
        this.$store.dispatch("users/clearForm");
        this.$store.commit("users/updateIndex", -1);
      });
    }
  },
  watch: {
    editedIndex() {
      if (this.$refs.userForm) {
        this.$refs.userForm.resetValidation();
      }
    }
  }
};
</script>

<style>
.custom_error_message {
  line-height: 16px;
}
</style>
